import { parseTimecodeParts } from '../../../utils/timecode';
import { extractOptionSlotsFromRows } from './maOptionSlots';
import { MaCsvRow, MaParseResult, MaRulerLane, MaTimeValue, MaWarning } from './maTypes';

/**
 * MA シーケンス CSV のパーサ。
 *
 * フォーマット:
 * - ヘッダー行必須・列順不問: media, start, end, offset, track, gain, color
 *   （media / start は必須列。未知の列は無視）
 * - `#` で始まる行はコメント行としてスキップ。ただし以下の特殊コメントは
 *   メタデータとして読み取る（serializeMaCsv が書き出すラウンドトリップ用）:
 *   - `# lanes: [{"index":1,"name":"VC","color":"#ff0030"}, ...]`
 *     （RPP のルーラーレーン。トラック順・レーン色フォールバックの元）
 *   - `# videotrack: "トラック名"`（タイムラインの動画トラックのラベル）
 * - start / end / offset: 整数のみなら絶対フレーム、`:` を含めば M:SS.FF
 *   （コメントのタイムコードと同じ形式。fps 換算はリンク時に行う）
 * - track: 1 始まりの整数、またはトラック名の文字列（オーディオ行用。
 *   トラック名はリンク時に数値 ID へ採番される）
 * - gain: dB の数値（"-6" / "+3.5" / "-6dB" を許容）
 * - color: クリップ表示色 `#rrggbb`（任意列。空は未指定）
 * - media が `(x)` / `[x]` の行はオプションスロットマーカーとして解釈し、
 *   最初の `[x]` 行以降は候補置き場（パレット領域）として本編行から除外する
 *   （RPP と同一規則・保存 CSV のラウンドトリップ用。詳細は maOptionSlots.ts）
 *
 * 行単位のエラーは警告として収集し、該当行のみスキップする（全体は止めない）。
 */

const KNOWN_COLUMNS = ['media', 'start', 'end', 'offset', 'track', 'gain', 'color'] as const;
type ColumnKey = (typeof KNOWN_COLUMNS)[number];

/** RFC4180 風の 1 行分割（ダブルクォート・エスケープ "" 対応） */
export function splitCsvLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map(f => f.trim());
}

/**
 * 時刻値のパース。
 * - "150" → 絶対フレーム
 * - "1:23.15" / "1:23" → タイムコード
 * - 空文字 / 不正値 → null
 */
export function parseMaTimeValue(raw: string): MaTimeValue | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^\d+$/.test(value)) {
    return { kind: 'frames', frames: parseInt(value, 10) };
  }
  const parts = parseTimecodeParts(value);
  if (parts) {
    return { kind: 'timecode', minutes: parts.minutes, seconds: parts.seconds, frames: parts.frames };
  }
  return null;
}

/** gain 列のパース（"-6" / "+3.5" / "-6dB" / "-6 db"）。不正は null */
function parseGainDb(raw: string): number | null {
  const value = raw.trim().replace(/\s*db$/i, '');
  if (!value) return null;
  if (!/^[+-]?\d+(\.\d+)?$/.test(value)) return null;
  return parseFloat(value);
}

/**
 * track 列のパース。
 * - 数値表記は 1 始まりの整数として解釈（0 以下・小数は不正 → null）
 * - それ以外の非空文字列はトラック名として受理
 */
function parseTrack(raw: string): number | string | null {
  const value = raw.trim();
  if (!value) return null;
  if (/^[+-]?\d+(\.\d+)?$/.test(value)) {
    if (!/^\d+$/.test(value)) return null;
    const n = parseInt(value, 10);
    return n >= 1 ? n : null;
  }
  return value;
}

/** color 列のパース（`#rrggbb` / `rrggbb`）。不正は null */
function parseColorHex(raw: string): string | null {
  const value = raw.trim();
  const m = value.match(/^#?([0-9a-fA-F]{6})$/);
  return m ? `#${m[1].toLowerCase()}` : null;
}

/** `# lanes:` コメントの JSON をルーラーレーン配列として検証・正規化する */
function parseLanesComment(json: string): MaRulerLane[] | null {
  try {
    const parsed: unknown = JSON.parse(json);
    if (!Array.isArray(parsed)) return null;
    const lanes: MaRulerLane[] = [];
    parsed.forEach((entry, i) => {
      if (entry === null || typeof entry !== 'object') return;
      const { index, name, color } = entry as { index?: unknown; name?: unknown; color?: unknown };
      if (typeof name !== 'string' || !name) return;
      lanes.push({
        index: typeof index === 'number' && Number.isFinite(index) ? index : i + 1,
        name,
        colorHex: typeof color === 'string' ? parseColorHex(color) : null,
      });
    });
    lanes.sort((a, b) => a.index - b.index);
    return lanes.length > 0 ? lanes : null;
  } catch {
    return null;
  }
}

export function parseMaCsv(text: string): MaParseResult {
  const warnings: MaWarning[] = [];
  const rows: MaCsvRow[] = [];

  // BOM 除去 + 改行分割（CRLF / CR / LF）
  const lines = text.replace(/^\uFEFF/, '').split(/\r\n|\r|\n/);

  let headerMap: Partial<Record<ColumnKey, number>> | null = null;
  let rulerLanes: MaRulerLane[] | undefined;
  let videoTrackName: string | undefined;

  for (let i = 0; i < lines.length; i++) {
    const lineNo = i + 1;
    const rawLine = lines[i];
    if (!rawLine.trim()) continue;
    if (rawLine.trimStart().startsWith('#')) {
      // 特殊コメント（serializeMaCsv が書くメタデータ）だけ読み取り、他は無視
      const lanesMatch = rawLine.match(/^\s*#\s*lanes:\s*(.+)$/);
      if (lanesMatch && rulerLanes === undefined) {
        rulerLanes = parseLanesComment(lanesMatch[1]) ?? undefined;
        continue;
      }
      const videoTrackMatch = rawLine.match(/^\s*#\s*videotrack:\s*(.+)$/);
      if (videoTrackMatch && videoTrackName === undefined) {
        try {
          const parsed: unknown = JSON.parse(videoTrackMatch[1]);
          if (typeof parsed === 'string' && parsed.trim()) videoTrackName = parsed.trim();
        } catch {
          // 不正な JSON は通常コメントとして無視
        }
      }
      continue;
    }

    const fields = splitCsvLine(rawLine);

    // ===== ヘッダー行 =====
    if (!headerMap) {
      const map: Partial<Record<ColumnKey, number>> = {};
      fields.forEach((name, idx) => {
        const key = name.toLowerCase() as ColumnKey;
        if ((KNOWN_COLUMNS as readonly string[]).includes(key) && map[key] === undefined) {
          map[key] = idx;
        }
      });
      if (map.media === undefined || map.start === undefined) {
        warnings.push({
          line: lineNo,
          message: 'ヘッダー行に media / start 列が見つかりません（1 行目にヘッダーが必要です）',
        });
        return { rows: [], warnings, optionSlots: [] };
      }
      headerMap = map;
      continue;
    }

    // ===== データ行 =====
    const getField = (key: ColumnKey): string => {
      const idx = headerMap?.[key];
      return idx !== undefined ? (fields[idx] ?? '').trim() : '';
    };

    const media = getField('media');
    if (!media) {
      warnings.push({ line: lineNo, message: 'media が空のため行をスキップしました' });
      continue;
    }

    const startRaw = getField('start');
    const start = parseMaTimeValue(startRaw);
    if (!start) {
      warnings.push({
        line: lineNo,
        message: `start "${startRaw}" を解釈できないため行をスキップしました（絶対フレーム or M:SS.FF）`,
      });
      continue;
    }

    // 以降はオプション列: 不正値は警告して未指定扱い（行はスキップしない）
    let end: MaTimeValue | null = null;
    const endRaw = getField('end');
    if (endRaw) {
      end = parseMaTimeValue(endRaw);
      if (!end) {
        warnings.push({ line: lineNo, message: `end "${endRaw}" を解釈できないため無視しました` });
      }
    }

    let offset: MaTimeValue | null = null;
    const offsetRaw = getField('offset');
    if (offsetRaw) {
      offset = parseMaTimeValue(offsetRaw);
      if (!offset) {
        warnings.push({ line: lineNo, message: `offset "${offsetRaw}" を解釈できないため無視しました` });
      }
    }

    let track: number | string | null = null;
    const trackRaw = getField('track');
    if (trackRaw) {
      track = parseTrack(trackRaw);
      if (track === null) {
        warnings.push({
          line: lineNo,
          message: `track "${trackRaw}" は 1 以上の整数またはトラック名で指定してください（無視しました）`,
        });
      }
    }

    let gainDb: number | null = null;
    const gainRaw = getField('gain');
    if (gainRaw) {
      gainDb = parseGainDb(gainRaw);
      if (gainDb === null) {
        warnings.push({ line: lineNo, message: `gain "${gainRaw}" を解釈できないため無視しました（dB の数値）` });
      }
    }

    let color: string | null = null;
    const colorRaw = getField('color');
    if (colorRaw) {
      color = parseColorHex(colorRaw);
      if (color === null) {
        warnings.push({
          line: lineNo,
          message: `color "${colorRaw}" を解釈できないため無視しました（#rrggbb）`,
        });
      }
    }

    rows.push({ line: lineNo, media, start, end, offset, track, gainDb, color });
  }

  if (!headerMap) {
    warnings.push({ line: null, message: 'CSV にヘッダー行が見つかりません' });
  } else if (rows.length === 0) {
    warnings.push({ line: null, message: 'CSV に有効なデータ行がありません' });
  }

  // ---- オプションマーカー行（`(x)` / `[x]`）の抽出とパレット領域の分離 ----
  const extracted = extractOptionSlotsFromRows(rows, warnings);

  return {
    rows: extracted.rows,
    warnings,
    optionSlots: extracted.optionSlots,
    rulerLanes,
    videoTrackName,
  };
}
